import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, ToggleButton, ToggleButtonGroup, Card,
  CardHeader, CardContent, Tooltip, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  Cell, AreaChart, Area, ReferenceLine, ComposedChart, Line,
} from 'recharts';
import { useAppTheme } from '../hooks/useAppTheme';
import { usePersistedState } from '../hooks/usePersistedState';
import { analyticsService, AnalyticsScope, AnalyticsSummary } from '../services/analytics.service';
import { formatCurrency } from '../utils/formatters';
import { CardSkeleton, KpiSkeleton } from '../components/common/Skeletons';
import AnalyticsInsights from '../components/analytics/AnalyticsInsights';
import OptimalRoiCard from '../components/analytics/OptimalRoiCard';
import BreakdownExplorer from '../components/analytics/BreakdownExplorer';
import TradesExplorer from '../components/analytics/TradesExplorer';
import CalendarPnlHeatmap from '../components/analytics/CalendarPnlHeatmap';

// value, label — 3650 ≈ "All"
const RANGES: { v: number; l: string }[] = [
  { v: 7, l: '7d' }, { v: 30, l: '30d' }, { v: 90, l: '90d' },
  { v: 180, l: '180d' }, { v: 365, l: '1Y' }, { v: 3650, l: 'All' },
];
const SCOPES: { v: AnalyticsScope; l: string; tip: string }[] = [
  { v: 'all', l: 'All', tip: 'Every closed trade' },
  { v: 'auto', l: 'Auto', tip: 'Signal/alert-driven trades the system placed (incl. autonomous RockBand). Populates as auto-trades close.' },
  { v: 'manual', l: 'Manual', tip: 'Chart-initiated, hand-added, or broker-imported trades' },
  { v: 'edge', l: 'Edge AI', tip: 'Real trades Edge AI (Pragya) had flagged — matched to a same-symbol/direction pick just before entry. Validates the engine\'s picks against your REAL P&L.' },
];

const AnalyticsPage: React.FC = () => {
  const { C } = useAppTheme();
  const [days, setDays] = usePersistedState('analytics.days', 90);
  const [scope, setScope] = usePersistedState<AnalyticsScope>('analytics.scope', 'all');
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{ dim: string; value: string; label: string } | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const summary = await analyticsService.getSummary(days, scope);
      setData(summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load analytics');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, scope]);
  // Clear drill-down when the scope/range changes (stale filter).
  useEffect(() => { setFilter(null); }, [days, scope]);

  const equity = useMemo(() => {
    if (!data) return [];
    let cum = 0;
    const bench = data.benchmark || [];
    return data.dailyPnl.map((d, i) => { cum += d.pnl; return { date: d.date.slice(5), equity: Number(cum.toFixed(2)), pnl: d.pnl, btc: bench[i]?.retPct ?? null }; });
  }, [data]);

  const Kpi: React.FC<{ label: string; value: string; tone?: 'bull' | 'bear' | 'warn' | 'neutral'; sub?: string; tooltip?: string }> =
    ({ label, value, tone = 'neutral', sub, tooltip }) => {
      const color = tone === 'bull' ? C.bull : tone === 'bear' ? C.bear : tone === 'warn' ? C.warn : 'text.primary';
      return (
        <Tooltip arrow title={tooltip || ''}>
          <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', height: '100%' }}>
            <Typography variant="caption" sx={{ fontSize: '0.64rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>{sub}</Typography>}
          </Box>
        </Tooltip>
      );
    };

  const pf = data && Number.isFinite(data.profitFactor) ? data.profitFactor.toFixed(2) : '∞';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Performance Analytics</Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup value={scope} exclusive size="small" onChange={(_, v) => v && setScope(v)}>
          {SCOPES.map(s => (
            <ToggleButton key={s.v} value={s.v}><Tooltip arrow title={s.tip}><span>{s.l}</span></Tooltip></ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup value={days} exclusive size="small" onChange={(_, v) => v && setDays(v)}>
          {RANGES.map(p => <ToggleButton key={p.v} value={p.v}>{p.l}</ToggleButton>)}
        </ToggleButtonGroup>
        <Tooltip title="Reload"><span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
      </Box>

      {error && <Paper sx={{ p: 2, mb: 2, bgcolor: C.bearMuted, color: C.bear, fontWeight: 600 }}>{error}</Paper>}

      {/* KPI band */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {loading && !data ? [0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <Grid key={i} size={{ xs: 6, sm: 3, md: 1.5 }}><KpiSkeleton /></Grid>
        )) : data ? (
          <>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Net P&L" tone={data.totalPnl >= 0 ? 'bull' : 'bear'} value={formatCurrency(data.totalPnl)} sub={`${data.totalTrades} trades`} /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Win rate" tone={data.winRate >= 50 ? 'bull' : 'warn'} value={`${data.winRate.toFixed(1)}%`} sub={`${data.wins}W / ${data.losses}L`} tooltip={`${data.wins} wins / ${data.losses} losses / ${data.scratches} scratches`} /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Expectancy" tone={data.expectancy >= 0 ? 'bull' : 'bear'} value={`${data.expectancy >= 0 ? '+' : ''}${data.expectancy.toFixed(2)}R`} tooltip="Expected R per trade = pWin·avgWinR + pLoss·avgLossR" /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Profit factor" tone={Number.isFinite(data.profitFactor) && data.profitFactor >= 1 ? 'bull' : 'bear'} value={pf} tooltip="Σ winning P&L ÷ |Σ losing P&L|" /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Avg R" tone={data.avgR >= 0 ? 'bull' : 'bear'} value={`${data.avgR >= 0 ? '+' : ''}${data.avgR.toFixed(2)}`} tooltip="Mean R-multiple across closed trades" /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Max DD" tone="bear" value={formatCurrency(data.maxDrawdown)} sub={`${data.maxDrawdownPercent.toFixed(0)}% of peak`} tooltip="Peak-to-trough drop in cumulative equity" /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Capture eff." tone={data.captureEfficiencyPct == null ? 'neutral' : data.captureEfficiencyPct >= 60 ? 'bull' : 'warn'} value={data.captureEfficiencyPct == null ? '—' : `${data.captureEfficiencyPct}%`} tooltip="Share of the favourable move (MFE) you actually captured. Populates as MFE data accrues." /></Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}><Kpi label="Hold (med)" value={`${data.medianHoldHours.toFixed(1)}h`} sub={`mean ${data.meanHoldHours.toFixed(1)}h`} /></Grid>
          </>
        ) : null}
      </Grid>

      {loading && !data ? <CardSkeleton rows={4} /> : data && (
        <Grid container spacing={2}>
          {/* Insights — centerpiece */}
          <Grid size={{ xs: 12 }}>
            <AnalyticsInsights insights={data.insights} headline={data.headlineVerdict} totalPnl={data.totalPnl} />
          </Grid>

          {/* Optimal ROI + rolling trend */}
          <Grid size={{ xs: 12, md: 6 }}><OptimalRoiCard roi={data.optimalRoi} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 1.5, height: '100%' }}>
              <CardHeader title={<Typography fontWeight={700}>Rolling expectancy (last 20 trades)</Typography>}
                subheader={<Typography variant="caption" color="text.secondary">Is the system improving over time?</Typography>} sx={{ pb: 0.5 }} />
              <CardContent>
                {data.rollingExpectancy.length < 2 ? (
                  <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>Need ≥20 closed trades to plot the trend.</Typography>
                ) : (
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={data.rollingExpectancy}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                        <XAxis dataKey="t" stroke={C.dim} fontSize={10} minTickGap={28} />
                        <YAxis yAxisId="r" stroke={C.dim} fontSize={10} tickFormatter={(v) => `${v}R`} />
                        <YAxis yAxisId="wr" orientation="right" stroke={C.dim} fontSize={10} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                        <RTooltip contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} />
                        <ReferenceLine yAxisId="r" y={0} stroke={C.dim} />
                        <Area yAxisId="r" type="monotone" dataKey="r" name="Expectancy R" stroke={C.chartLine1} fill={C.chartLine1} fillOpacity={0.18} />
                        <Line yAxisId="wr" type="monotone" dataKey="winRate" name="Win %" stroke={C.warn} dot={false} strokeWidth={1.5} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Daily P&L calendar heatmap */}
          <Grid size={{ xs: 12 }}><CalendarPnlHeatmap dailyPnl={data.dailyPnl} /></Grid>

          {/* Breakdown + Trades explorer */}
          <Grid size={{ xs: 12 }}><BreakdownExplorer data={data} active={filter} onSelect={setFilter} /></Grid>
          <Grid size={{ xs: 12 }}><TradesExplorer days={days} scope={scope} filter={filter} onClearFilter={() => setFilter(null)} /></Grid>

          {/* Charts */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ borderRadius: 1.5 }}>
              <CardHeader title={<Typography fontWeight={700}>Daily P&amp;L</Typography>} sx={{ pb: 0.5 }} />
              <CardContent>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.dailyPnl.map(d => ({ ...d, dateShort: d.date.slice(5) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                      <XAxis dataKey="dateShort" stroke={C.dim} fontSize={11} minTickGap={20} />
                      <YAxis stroke={C.dim} fontSize={11} />
                      <RTooltip contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} formatter={(value: any) => formatCurrency(Number(value))} />
                      <ReferenceLine y={0} stroke={C.dim} />
                      <Bar dataKey="pnl" radius={[2, 2, 0, 0]}>
                        {data.dailyPnl.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? C.chartBull : C.chartBear} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 1.5 }}>
              <CardHeader title={<Typography fontWeight={700}>Equity curve</Typography>}
                subheader={data.benchmarkRetPct != null
                  ? <Typography variant="caption" color="text.secondary">Your ₹ P&amp;L vs <span style={{ color: C.warn }}>buy-&-hold BTC {data.benchmarkRetPct >= 0 ? '+' : ''}{data.benchmarkRetPct}%</span> over this window</Typography>
                  : undefined}
                sx={{ pb: 0.5 }} />
              <CardContent>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={equity}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                      <XAxis dataKey="date" stroke={C.dim} fontSize={11} minTickGap={24} />
                      <YAxis yAxisId="eq" stroke={C.dim} fontSize={11} />
                      <YAxis yAxisId="pct" orientation="right" stroke={C.warn} fontSize={10} tickFormatter={(v) => `${v}%`} width={40} />
                      <RTooltip contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
                        formatter={(value: any, name: string) => name === 'BTC buy-hold' ? [`${Number(value).toFixed(1)}%`, name] : [formatCurrency(Number(value)), 'Equity (₹)']} />
                      <ReferenceLine yAxisId="eq" y={0} stroke={C.dim} />
                      <Area yAxisId="eq" type="monotone" dataKey="equity" stroke={C.chartLine1} fill={C.chartLine1} fillOpacity={0.22} name="Equity (₹)" />
                      {data.benchmarkRetPct != null && <Line yAxisId="pct" type="monotone" dataKey="btc" stroke={C.warn} strokeWidth={1.5} dot={false} strokeDasharray="5 3" name="BTC buy-hold" connectNulls />}
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 1.5 }}>
              <CardHeader title={<Typography fontWeight={700}>R-multiple distribution</Typography>}
                subheader={<Typography variant="caption" color="text.secondary">Realised P&amp;L ÷ initial SL risk per trade</Typography>} sx={{ pb: 0.5 }} />
              <CardContent>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.rDistribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                      <XAxis dataKey="bucket" stroke={C.dim} fontSize={10} />
                      <YAxis stroke={C.dim} fontSize={11} allowDecimals={false} />
                      <RTooltip contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} />
                      <Bar dataKey="count">
                        {data.rDistribution.map((b, i) => <Cell key={i} fill={b.bucket.includes('-') ? C.chartBear : C.chartBull} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 1.5 }}>
              <CardHeader title={<Typography fontWeight={700}>Hold-time histogram</Typography>}
                subheader={<Typography variant="caption" color="text.secondary">Mean {data.meanHoldHours.toFixed(1)}h · Median {data.medianHoldHours.toFixed(1)}h</Typography>} sx={{ pb: 0.5 }} />
              <CardContent>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.holdHistogram}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                      <XAxis dataKey="bucket" stroke={C.dim} fontSize={11} />
                      <YAxis stroke={C.dim} fontSize={11} allowDecimals={false} />
                      <RTooltip contentStyle={{ background: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} />
                      <Bar dataKey="count" fill={C.chartLine1} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticsPage;
